import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  DollarSign,
  Clock,
  MessageSquare,
  Send,
  CheckCircle2,
  MapPin,
  Calendar,
  User,
} from "lucide-react";
import { doc, onSnapshot, getDoc, addDoc, collection, updateDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../firebase";
import useCategories from "../../hooks/useCategories";
import { Button } from "../ui/Button";

function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatDate(value) {
  if (!value) return "Not specified";

  try {
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleDateString();
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "Not specified";
    return d.toLocaleDateString();
  } catch {
    return "Not specified";
  }
}

function formatTime(value) {
  if (!value) return "Not specified";
  return String(value);
}

export default function SendOffer() {
  const navigate = useNavigate();
  
  const { requestId } = useParams()

  const { categories = [] } = useCategories();

  const [request, setRequest] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const [offeredPrice, setOfferedPrice] = useState("");
  const [estimatedDuration, setEstimatedDuration] = useState("");
  const [durationUnit, setDurationUnit] = useState("hours");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!requestId) {
      setLoading(false);
      return;
    }

    const unsub = onSnapshot(
      doc(db, "serviceRequests", requestId),
      async (snap) => {
        if (!snap.exists()) {
          setRequest(null);
          setCustomer(null);
          setLoading(false);
          return;
        }

        const requestData = { id: snap.id, ...snap.data() };
        setRequest(requestData);

        try {
          if (requestData.customerId) {
            const customerSnap = await getDoc(doc(db, "customers", requestData.customerId));
            if (customerSnap.exists()) {
              setCustomer({ id: customerSnap.id, ...customerSnap.data() });
            } else {
              setCustomer(null);
            }
          } else {
            setCustomer(null);
          }
        } catch (err) {
          console.error("Failed to load customer:", err);
          setCustomer(null);
        }

        setLoading(false);
      },
      (err) => {
        console.error("SendOffer listener error:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [requestId]);

  const category = useMemo(() => {
    if (!request?.categoryId) return null;
    return categories.find((c) => c.id === request.categoryId) || null;
  }, [categories, request?.categoryId]);

  const subcategory = useMemo(() => {
    if (!category || !request?.subcategoryId) return null;
    return category.subcategories?.find((s) => s.id === request.subcategoryId) || null;
  }, [category, request?.subcategoryId]);

  const customerBudget = safeNumber(request?.budget, 0);
  const numericOffer = safeNumber(offeredPrice, 0);

  const handleSubmitOffer = async (e) => {
    e.preventDefault();

    if (!auth.currentUser) {
      alert("You must be logged in first.");
      return;
    }

    if (!request) {
      alert("Service request not found.");
      return;
    }

    if (!offeredPrice || !estimatedDuration || !message.trim()) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      setSubmitting(true);

      await addDoc(collection(db, "taskerOffers"), {
        taskerId: auth.currentUser.uid,
        message: message.trim(),
        estimatedDuration: `${estimatedDuration} ${durationUnit}`,
        requestedId: request.id,
        categoryId: request.categoryId || "",
        subcategoryId: request.subcategoryId ? [request.subcategoryId] : [],
        offeredPrice: safeNumber(offeredPrice, 0),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await updateDoc(doc(db, "serviceRequests", request.id), {
        updatedAt: serverTimestamp(),
        status: request.status === "draft" ? "open" : request.status || "open",
      });

      setShowSuccess(true);
    } catch (err) {
      console.error("Failed to send offer:", err);
      alert("Failed to send offer. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-gray-600">Loading request...</div>
      </div>
    );
  }

  if (!requestId || !request) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md w-full text-center">
          <h2 className="text-xl text-[#1E3A8A] mb-2">Request not found</h2>
          <p className="text-gray-600 mb-4">The service request could not be loaded.</p>
          <Button onClick={() => navigate("/tasker/dashboard")} className="bg-[#006C35] hover:bg-[#005428]">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center animate-in zoom-in duration-300">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={40} className="text-[#006C35]" />
          </div>

          <h2 className="text-2xl text-[#1E3A8A] mb-2">Offer Sent Successfully!</h2>

          <p className="text-gray-600 mb-4">
            Your offer has been sent to {customer?.name || "the customer"}.
          </p>

          <Button
            onClick={() => navigate("/tasker/dashboard")}
            className="bg-[#006C35] hover:bg-[#005428]"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900">
            <ArrowLeft size={24} />
          </button>
          <h2 className="text-[#1E3A8A]">Send Your Offer</h2>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-start justify-between mb-4 gap-4">
            <div>
              <h3 className="text-xl text-[#1E3A8A] mb-2">{request.title || "Untitled Request"}</h3>

              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {category && (
                  <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm">
                    {category.icon ? `${category.icon} ` : ""}
                    {category.name}
                  </span>
                )}

                {subcategory && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm">
                    {subcategory.name}
                  </span>
                )}
              </div>
            </div>

            <div className="text-right shrink-0">
              <div className="text-sm text-gray-500 mb-1">Customer Budget</div>
              <div className="text-2xl text-purple-400">{customerBudget} SAR</div>
            </div>
          </div>

          <p className="text-gray-600 mb-4">{request.description || "No description provided."}</p>

          <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-3 text-sm">
              <User size={18} className="text-gray-400" />
              <div>
                <div className="text-xs text-gray-500">Posted by</div>
                <div className="text-gray-700">{customer?.name || "Unknown customer"}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <MapPin size={18} className="text-gray-400" />
              <div>
                <div className="text-xs text-gray-500">Location</div>
                <div className="text-gray-700">{request.locationText || "Not specified"}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <Calendar size={18} className="text-gray-400" />
              <div>
                <div className="text-xs text-gray-500">Preferred Date</div>
                <div className="text-gray-700">{formatDate(request.preferredDate)}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <Clock size={18} className="text-gray-400" />
              <div>
                <div className="text-xs text-gray-500">Preferred Time</div>
                <div className="text-gray-700">{formatTime(request.preferredTime)}</div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmitOffer} className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg text-[#1E3A8A] mb-6">Your Offer Details</h3>

          <div className="space-y-6">
            <div>
              <label className="block text-sm text-gray-700 mb-2">
                Your Offered Price <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <DollarSign size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  value={offeredPrice}
                  onChange={(e) => setOfferedPrice(e.target.value)}
                  placeholder="Enter your price"
                  className="w-full pl-10 pr-16 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35] text-lg"
                  required
                  min="1"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                  SAR
                </span>
              </div>

              {offeredPrice && (
                <div className="mt-2 text-sm">
                  {numericOffer < customerBudget ? (
                    <span className="text-green-600">
                      ✓ Your price is {customerBudget - numericOffer} SAR below customer budget
                    </span>
                  ) : numericOffer === customerBudget ? (
                    <span className="text-blue-600">✓ Matches customer budget</span>
                  ) : (
                    <span className="text-orange-600">
                      ⚠ Your price is {numericOffer - customerBudget} SAR above customer budget
                    </span>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-2">
                Estimated Duration <span className="text-red-500">*</span>
              </label>

              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Clock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="number"
                    value={estimatedDuration}
                    onChange={(e) => setEstimatedDuration(e.target.value)}
                    placeholder="e.g., 2"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
                    required
                    min="1"
                  />
                </div>

                <select
                  value={durationUnit}
                  onChange={(e) => setDurationUnit(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35] bg-white"
                >
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>

              <p className="text-xs text-gray-500 mt-2">How long do you estimate this job will take?</p>
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-2">
                Message to Customer <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <MessageSquare size={20} className="absolute left-3 top-3 text-gray-400" />

                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Introduce yourself and explain why you're the best fit for this job..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35] min-h-35"
                  required
                  maxLength={500}
                />
              </div>

              <div className="flex justify-between items-center mt-2">
                <p className="text-xs text-gray-500">Be professional and highlight your expertise</p>
                <p className="text-xs text-gray-400">{message.length}/500</p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <CheckCircle2 size={16} />
                Tips for a Winning Offer
              </h4>

              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Be competitive with your pricing while valuing your expertise</li>
                <li>• Provide a realistic time estimate to set proper expectations</li>
                <li>• Personalize your message and show you understand the job</li>
                <li>• Highlight your relevant experience and certifications</li>
                <li>• Ask clarifying questions if needed</li>
              </ul>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex-1"
                disabled={submitting}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                className="flex-1 bg-[#006C35] hover:bg-[#005428] py-6 text-base"
                disabled={submitting}
              >
                <Send size={20} className="mr-2" />
                {submitting ? "Sending..." : "Send Offer"}
              </Button>
            </div>
          </div>
        </form>

        {offeredPrice && estimatedDuration && message && (
          <div className="bg-linear-to-r from-green-50 to-blue-50 rounded-xl border border-green-200 p-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Offer Summary</h4>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-gray-500 mb-1">Offered Price</div>
                <div className="text-lg text-[#006C35] font-semibold">{offeredPrice} SAR</div>
              </div>

              <div>
                <div className="text-xs text-gray-500 mb-1">Estimated Duration</div>
                <div className="text-lg text-[#1E3A8A] font-semibold">
                  {estimatedDuration} {durationUnit}
                </div>
              </div>

              <div>
                <div className="text-xs text-gray-500 mb-1">Message Length</div>
                <div className="text-lg text-gray-700 font-semibold">{message.length} characters</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}